/** Busca de modelos GGUF no Hugging Face.
 *
 * Roda inteiro no WebView, sem passar pela ponte nativa, porque a API do Hugging
 * Face manda `Access-Control-Allow-Origin` — conferido em 8/ago/2026 a partir da
 * origem do app. Só o download continua nativo, e por motivo diferente: 800 MB
 * não cabem num Blob sem duplicar na memória da aba, e o download precisa
 * sobreviver a quem sair da tela.
 *
 * O que este módulo NÃO faz, de propósito: decidir o que cabe no aparelho. Ele
 * devolve tamanhos; quem compara com a memória é a tela, que tem os números do
 * aparelho. Misturar as duas coisas daria um módulo que precisa saber de
 * Android para listar arquivo.
 */

export interface RepoGguf {
  id: string;
  downloads: number;
  likes: number;
  /** Repositório que exige aceitar licença e token pessoal. Vem `null` ou
   *  ausente quando é livre; qualquer outra coisa significa fechado. */
  fechado: boolean;
  /** Tamanhos dos .gguf, preenchidos depois pela `medir()`.
   *
   *  Ficam fora da busca porque a API de busca não os traz: descobrir o
   *  tamanho exige uma consulta por repositório. Vêm num segundo momento para
   *  a lista aparecer rápido e os números chegarem em seguida, em vez de a
   *  tela esperar vinte consultas antes de mostrar qualquer coisa. */
  menorMb?: number;
  maiorMb?: number;
  arquivos?: number;
}

/* A classificação por nome mora em `gguf-nomes.ts`: a busca e a lista do
 * aparelho precisam da MESMA resposta, e duas cópias da regra foi exatamente o
 * defeito — a busca aprendeu a reconhecer peças auxiliares, a lista do
 * aparelho não, e o arquivo recusado lá continuava com botão de carregar aqui.
 */
export type { PapelDoArquivo } from "./gguf-nomes.js";


export interface ArquivoGguf {
  nome: string;
  bytes: number;
  papel: PapelDoArquivo;
  /** Por que este arquivo não serve sozinho. Vazio quando serve. */
  aviso: string;
  /** A parte do nome que descreve a quantização: Q4_K_M, IQ4_XS, Q8_0…
   *
   *  É o que decide se cabe, mais que o nome do modelo — um 7B em Q4 cabe onde
   *  um 3B em Q8 não cabe. Extraído do nome do arquivo porque é onde a
   *  convenção do ecossistema a coloca; vazio quando não casa, e aí a pessoa lê
   *  o nome inteiro. */
  quantizacao: string;
}

import { papelPeloNome } from "./gguf-nomes.js";

const API = "https://huggingface.co/api";

/** Modelos GGUF que casam com o termo, dos mais baixados para os menos.
 *
 *  `filter=gguf` é o que separa o que dá para rodar aqui do resto do Hugging
 *  Face. A imensa maioria dos repositórios de lá é `safetensors`, que o
 *  llama.cpp não abre — e procurar meia hora um modelo que nunca ia carregar é
 *  a frustração que este filtro evita. */
export async function buscar(termo: string, limite = 20): Promise<RepoGguf[]> {
  const q = new URLSearchParams({
    search: termo,
    filter: "gguf",
    sort: "downloads",
    direction: "-1",
    limit: String(limite),
  });
  const r = await fetch(`${API}/models?${q}`);
  if (!r.ok) throw new Error(`o Hugging Face respondeu ${r.status}`);
  const bruto = (await r.json()) as Array<Record<string, unknown>>;
  return bruto.map((m) => ({
    id: String(m.id ?? ""),
    downloads: Number(m.downloads) || 0,
    likes: Number(m.likes) || 0,
    // `gated` vem como false, null, "auto" ou "manual". Só `false`/ausente é
    // livre — tratar "auto" como livre daria um download de 800 MB terminando
    // em 401.
    fechado: !!m.gated,
  }));
}

/** Os .gguf de um repositório, do menor para o maior.
 *
 *  Ordem crescente porque é a ordem em que se deve tentar: quem começa pelo
 *  maior descobre o limite do aparelho com o app fechando, quem começa pelo
 *  menor descobre com um número na tela. Mesmo princípio do banco de provas. */
export async function arquivos(repo: string): Promise<ArquivoGguf[]> {
  const r = await fetch(`${API}/models/${repo}/tree/main`);
  if (!r.ok) throw new Error(`não deu para listar ${repo}: ${r.status}`);
  const bruto = (await r.json()) as Array<Record<string, unknown>>;
  return bruto
    .filter((f) => String(f.path ?? "").toLowerCase().endsWith(".gguf"))
    .map((f) => {
      const nome = String(f.path);
      // O tamanho real está em `lfs.size`; o `size` de fora é o do ponteiro do
      // Git LFS, uns poucos bytes. Ler o de fora faria um modelo de 800 MB
      // aparecer como "0 MB" e passar em qualquer checagem de espaço.
      const lfs = f.lfs as { size?: number } | undefined;
      const { papel, aviso } = papelPeloNome(nome);
      return {
        nome,
        bytes: Number(lfs?.size ?? f.size ?? 0),
        papel,
        aviso,
        quantizacao: (nome.match(/[.-](I?Q\d[^.]*|F?P?16|BF16|F32)\./i)?.[1] ?? "").toUpperCase(),
      };
    })
    .filter((a) => a.bytes > 0)
    .sort((a, b) => a.bytes - b.bytes);
}

/** Preenche os tamanhos de cada repositório, em paralelo e com folga.
 *
 *  Chamado depois da busca, e não durante: a lista aparece na hora com os
 *  nomes, e os tamanhos vão chegando. O contrário — esperar vinte consultas
 *  para mostrar a primeira linha — faria a busca parecer travada.
 *
 *  `aoMedir` é chamado por repositório, para a tela atualizar aquela linha
 *  sozinha em vez de esperar todas.
 *
 *  Falha de um não derruba os outros: repositório que não responde fica sem
 *  número, o que é honesto — não sabemos o tamanho, e inventar seria pior. */
export async function medir(
  repos: RepoGguf[],
  aoMedir: (r: RepoGguf) => void,
): Promise<void> {
  // De três em três: o Hugging Face limita requisições por minuto, e vinte de
  // uma vez é o caminho para tomar 429 e ficar sem número nenhum.
  const lote = 3;
  for (let i = 0; i < repos.length; i += lote) {
    await Promise.all(
      repos.slice(i, i + lote).map(async (r) => {
        try {
          const todos = await arquivos(r.id);
          // Só os que são modelo de verdade entram na faixa. Incluir o
          // projetor e o rascunho faria um repositório de 15 GB anunciar "a
          // partir de 230 MB" — que foi exatamente a informação que levou
          // alguém a baixar a peça errada.
          const arqs = todos.filter((a) => a.papel === "modelo");
          if (!arqs.length) return;
          r.arquivos = arqs.length;
          r.menorMb = Math.round(arqs[0]!.bytes / 1048576);
          r.maiorMb = Math.round(arqs[arqs.length - 1]!.bytes / 1048576);
          aoMedir(r);
        } catch {
          // Sem número para este; os outros seguem.
        }
      }),
    );
  }
}

/** Endereço direto do arquivo. `resolve` segue para o CDN — o lado nativo trata
 *  o redirecionamento. */
export function urlDe(repo: string, arquivo: string): string {
  return `https://huggingface.co/${repo}/resolve/main/${encodeURIComponent(arquivo)}`;
}

/** Ponte para os testes, no mesmo padrão dos demais módulos. */
declare global {
  interface Window {
    __wuweiHf: { buscar: typeof buscar; arquivos: typeof arquivos; urlDe: typeof urlDe };
  }
}

if (typeof window !== "undefined") {
  window.__wuweiHf = { buscar, arquivos, urlDe };
}
